import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useSseSubscription } from '../context/SseContext';
import NavHeader from '../components/NavHeader';
import { getProjection, getApproverInvoiceIds } from '../api';

// `cls` — typography utility class (src/typography.css): 'num' for amounts
// (tabular digits), 'code' for identifiers (tabular digits + slashed zero).
const COLUMNS = [
  { key: 'invoice_date', label: 'Invoice Date', sortable: true },
  { key: 'payment_date', label: 'Payment Date', sortable: true },
  { key: 'quarter', label: 'Quarter', sortable: true },
  { key: 'source', label: 'Source', sortable: true },
  { key: 'owner_name', label: 'Requestor', sortable: true },
  { key: 'jurisdiction', label: 'Jurisdiction', sortable: true },
  { key: 'currency', label: 'Currency', sortable: true },
  { key: 'gl_company_code', label: 'Company Code', sortable: true, cls: 'code' },
  { key: 'gl_cost_center', label: 'Cost Center', sortable: true, cls: 'code' },
  { key: 'gl_account', label: 'GL Account', sortable: true, cls: 'code' },
  { key: 'gl_internal_order', label: 'Internal Order', sortable: true, cls: 'code' },
  { key: 'area', label: 'Area', sortable: true },
  { key: 'ascent_matters', label: 'ASCENT Matter', sortable: true, cls: 'code' },
  { key: 'project_name', label: 'Project', sortable: true },
  { key: 'vendor_name', label: 'Vendor', sortable: true },
  { key: 'amount', label: 'Amount', sortable: true, align: 'right', cls: 'num' },
  { key: 'invoice_reference', label: 'Invoice Ref', sortable: true, cls: 'code' },
  { key: 'comments', label: 'Comments', sortable: false },
  { key: 'status', label: 'Status', sortable: true },
];

const STATUS_LABELS = {
  received: 'Received',
  under_review: 'Under Review',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  submitted_to_ap: 'Submitted to AP',
  payment_processing: 'Payment Processing',
  posted_in_sap: 'Posted in SAP',
  reconciled: 'Reconciled',
  rejected: 'Rejected',
};

function formatCurrency(amount, currency) {
  if (amount == null) return '';
  const num = parseFloat(amount);
  if (isNaN(num)) return '';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(num);
  } catch {
    return `${num.toFixed(2)} ${currency || ''}`.trim();
  }
}

function PaymentTracker() {
  const { currentUser } = useUser();
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastHash, setLastHash] = useState(null);

  // Filters
  const [filterQuarter, setFilterQuarter] = useState('All');
  const [filterArea, setFilterArea] = useState('All');
  const [filterOwner, setFilterOwner] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterVendor, setFilterVendor] = useState('');
  const [filterProject, setFilterProject] = useState('');

  // Approver filtering
  const [approverInvoiceIds, setApproverInvoiceIds] = useState(null);

  useEffect(() => {
    if (currentUser?.role === 'approver') {
      getApproverInvoiceIds().then(res => {
        const ids = new Set(res.data.invoice_ids);
        setApproverInvoiceIds(ids);
      }).catch(() => {});
    }
  }, [currentUser?.role]);

  // Sort
  const [sortKey, setSortKey] = useState('invoice_date');
  const [sortDir, setSortDir] = useState('desc');

  const loadData = useCallback(async () => {
    try {
      const res = await getProjection('payment_tracker');
      const data = res.data;
      if (data.hash && data.hash === lastHash) return; // no change
      setLastHash(data.hash);
      const invs = data.payload?.invoices ?? [];
      setInvoices(invs);
      setError(null);
    } catch (err) {
      setError('Failed to load payment tracker data');
    } finally {
      setLoading(false);
    }
  }, [lastHash]);

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // SSE — refresh via the shared connection (loadData early-returns on an
  // unchanged hash, so redundant messages are cheap).
  useSseSubscription(() => loadData());

  // Derive unique values for filter dropdowns
  const uniqueQuarters = useMemo(() => {
    const s = new Set(invoices.map((i) => i.quarter).filter(Boolean));
    return ['Q1', 'Q2', 'Q3', 'Q4'].filter((q) => s.has(q));
  }, [invoices]);

  const uniqueAreas = useMemo(() => {
    const s = new Set(invoices.map((i) => i.area).filter(Boolean));
    return [...s].sort();
  }, [invoices]);

  const uniqueOwners = useMemo(() => {
    const s = new Set(invoices.map((i) => i.owner_name).filter(Boolean));
    return [...s].sort();
  }, [invoices]);

  const uniqueStatuses = useMemo(() => {
    const s = new Set(invoices.map((i) => i.status).filter(Boolean));
    return [...s].sort();
  }, [invoices]);

  // Filtered invoices
  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) => {
      // Approver role: only show invoices assigned to them for approval
      if (approverInvoiceIds && !approverInvoiceIds.has(inv.id)) return false;
      if (filterQuarter !== 'All' && inv.quarter !== filterQuarter) return false;
      if (filterArea !== 'All' && (inv.area || '') !== filterArea) return false;
      if (filterOwner !== 'All' && (inv.owner_name || '') !== filterOwner) return false;
      if (filterStatus !== 'All' && (inv.status || '') !== filterStatus) return false;
      if (filterVendor && !(inv.vendor_name || '').toLowerCase().includes(filterVendor.toLowerCase())) return false;
      if (filterProject && !(inv.project_name || '').toLowerCase().includes(filterProject.toLowerCase())) return false;
      return true;
    });
  }, [invoices, approverInvoiceIds, filterQuarter, filterArea, filterOwner, filterStatus, filterVendor, filterProject]);

  // Sorted invoices
  const sortedInvoices = useMemo(() => {
    const sorted = [...filteredInvoices];
    sorted.sort((a, b) => {
      let aVal = a[sortKey] ?? '';
      let bVal = b[sortKey] ?? '';
      if (sortKey === 'amount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredInvoices, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleExportCsv = () => {
    const headers = COLUMNS.map((c) => c.label);
    const rows = sortedInvoices.map((inv) =>
      COLUMNS.map((c) => {
        let val = inv[c.key] ?? '';
        if (c.key === 'status') val = STATUS_LABELS[val] || val;
        // Escape double quotes in CSV values
        val = String(val).replace(/"/g, '""');
        return `"${val}"`;
      })
    );
    const csvContent = [headers.map((h) => `"${h}"`).join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const today = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `payment_tracker_${today}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderCellValue = (inv, col) => {
    const val = inv[col.key];
    if (col.key === 'amount') {
      return formatCurrency(val, inv.currency);
    }
    if (col.key === 'invoice_reference') {
      if (val && String(val).startsWith('http')) {
        return (
          <a
            href={val}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="tracker-link"
          >
            {val}
          </a>
        );
      }
      return val || '';
    }
    if (col.key === 'status') {
      return STATUS_LABELS[val] || val || '';
    }
    return val || '';
  };

  if (loading) {
    return (
      <div className="board-loading">
        <div className="loading-spinner">Loading payment tracker...</div>
      </div>
    );
  }

  return (
    <div className="tracker-page">
      <NavHeader />

      {/* Page Content */}
      <div className="tracker-content">
        <div className="tracker-title-row">
          <h2 className="tracker-heading">Payment Tracker</h2>
          <button className="btn btn-primary" onClick={handleExportCsv}>
            Export to Excel
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Filters */}
        <div className="tracker-filters">
          <label className="tracker-filter">
            <span className="tracker-filter-label">Quarter</span>
            <select value={filterQuarter} onChange={(e) => setFilterQuarter(e.target.value)}>
              <option value="All">All</option>
              {uniqueQuarters.map((q) => (
                <option key={q} value={q}>{q}</option>
              ))}
            </select>
          </label>
          <label className="tracker-filter">
            <span className="tracker-filter-label">Area</span>
            <select value={filterArea} onChange={(e) => setFilterArea(e.target.value)}>
              <option value="All">All</option>
              {uniqueAreas.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </label>
          <label className="tracker-filter">
            <span className="tracker-filter-label">Requestor</span>
            <select value={filterOwner} onChange={(e) => setFilterOwner(e.target.value)}>
              <option value="All">All</option>
              {uniqueOwners.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </label>
          <label className="tracker-filter">
            <span className="tracker-filter-label">Status</span>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="All">All</option>
              {uniqueStatuses.map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
              ))}
            </select>
          </label>
          <label className="tracker-filter">
            <span className="tracker-filter-label">Vendor</span>
            <input
              type="text"
              placeholder="Search vendor..."
              value={filterVendor}
              onChange={(e) => setFilterVendor(e.target.value)}
            />
          </label>
          <label className="tracker-filter">
            <span className="tracker-filter-label">Project</span>
            <input
              type="text"
              placeholder="Search project..."
              value={filterProject}
              onChange={(e) => setFilterProject(e.target.value)}
            />
          </label>
        </div>

        {/* Table */}
        <div className="tracker-table-wrapper">
          <table className="tracker-table">
            <thead>
              <tr>
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    className={`${col.sortable ? 'sortable' : ''} ${col.align === 'right' ? 'text-right' : ''}`}
                    onClick={() => col.sortable && handleSort(col.key)}
                  >
                    {col.label}
                    {sortKey === col.key && (
                      <span className="sort-indicator">{sortDir === 'asc' ? ' \u25B2' : ' \u25BC'}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedInvoices.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="tracker-empty">
                    {invoices.length === 0
                      ? 'No invoices yet. Upload invoices from the Kanban Board.'
                      : 'No invoices match the current filters.'}
                  </td>
                </tr>
              ) : (
                sortedInvoices.map((inv) => (
                  <tr
                    key={inv.id}
                    className="tracker-row"
                    onClick={() => navigate(`/invoice/${inv.id}`)}
                  >
                    {COLUMNS.map((col) => (
                      <td
                        key={col.key}
                        className={[col.align === 'right' ? 'text-right' : '', col.cls || '']
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {renderCellValue(inv, col)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="tracker-footer">
          <span className="tracker-count">
            Showing {sortedInvoices.length} of {invoices.length} invoices
          </span>
        </div>
      </div>
    </div>
  );
}

export default PaymentTracker;
